import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import ReactDom from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import "./index.scss";
import PopupOverlay from "../Background/PopupOverlay";
import TabbedLayout from "../TabbedLayout";
import Button from "../../atoms/Button";
import Card from "../../molecules/Card";
import PrivacySettings from "../../molecules/PrivacySettings";
import SecuritySettings from "../../molecules/SecuritySettings";
import ChangePasswordModal from "../../molecules/ChangePasswordModal";
import ActiveSessionsModal from "../../molecules/ActiveSessionsModal";
import AccountDeletionModal from "../../molecules/AccountDeletionModal";
import AppearanceSettings from "../../molecules/AppearanceSettings";
import { accountService } from "../../../services/accountService";
import { userProfileService } from "../../../services/userProfileService";
import { useUserPreferences } from "../../../store/UserPreferencesContext";
import {
    buildSettingsPayload,
    normalizeSettings,
} from "../../../constants/profileSettings";
import { clearUserScopedClientState } from "../../../services/clientSessionLifecycle";

const EditProfile = ({ onClose }) => {
    const dispatch = useDispatch();
    const currentUser = useSelector((state) => state.auth.user);
    const { t } = useTranslation();
    const [settings, setSettings] = useState(() => normalizeSettings());
    const [isFetching, setIsFetching] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isStartingPasswordSetup, setIsStartingPasswordSetup] = useState(false);
    const [feedback, setFeedback] = useState(null);
    const [showChangePassword, setShowChangePassword] = useState(false);
    const [showActiveSessions, setShowActiveSessions] = useState(false);
    const [showAccountDeletion, setShowAccountDeletion] = useState(false);
    const {
        settings: appliedSettings,
        setLanguagePreference,
        setThemePreference,
        syncSettingsSnapshot,
    } = useUserPreferences();
    const appliedSettingsRef = useRef(normalizeSettings(appliedSettings));
    const committedSettingsRef = useRef(normalizeSettings(appliedSettings));
    const draftSettingsRef = useRef(normalizeSettings());

    useEffect(() => {
        appliedSettingsRef.current = normalizeSettings(appliedSettings);
    }, [appliedSettings]);

    useEffect(() => {
        let isMounted = true;

        const fetchSettings = async () => {
            if (!currentUser?.id) {
                if (isMounted) {
                    const fallback = appliedSettingsRef.current;
                    committedSettingsRef.current = fallback;
                    draftSettingsRef.current = fallback;
                    setIsFetching(false);
                    setSettings(fallback);
                }
                return;
            }

            if (isMounted) {
                setIsFetching(true);
                setFeedback(null);
            }

            try {
                const data = await userProfileService.getOwnProfile(currentUser.id);
                if (isMounted) {
                    const normalized = normalizeSettings(data?.settings);
                    committedSettingsRef.current = normalized;
                    draftSettingsRef.current = normalized;
                    setSettings(normalized);
                    syncSettingsSnapshot(normalized);
                }
            } catch (err) {
                console.error("Error fetching settings:", err);
                if (isMounted) {
                    setFeedback({ type: "error", messageKey: "profile.feedback.loadError" });
                    const fallback = appliedSettingsRef.current;
                    committedSettingsRef.current = fallback;
                    draftSettingsRef.current = fallback;
                    setSettings(fallback);
                }
            } finally {
                if (isMounted) {
                    setIsFetching(false);
                }
            }
        };

        fetchSettings();

        return () => {
            isMounted = false;
        };
    }, [currentUser?.id, syncSettingsSnapshot]);

    const handleSettingsChange = (section, newSettings) => {
        const normalized = normalizeSettings({
            ...draftSettingsRef.current,
            [section]: {
                ...draftSettingsRef.current[section],
                ...newSettings,
            },
        });

        draftSettingsRef.current = normalized;
        setSettings(normalized);
        setFeedback(null);

        if (section === "appearance") {
            if (Object.prototype.hasOwnProperty.call(newSettings, "language")) {
                setLanguagePreference(normalized.appearance.language, { persist: false });
            }
            if (Object.prototype.hasOwnProperty.call(newSettings, "theme")) {
                setThemePreference(normalized.appearance.theme, { persist: false });
            }
        }
    };

    const handleCancelClick = () => {
        draftSettingsRef.current = committedSettingsRef.current;
        syncSettingsSnapshot(committedSettingsRef.current);
        onClose();
    };

    const handleSaveClick = async () => {
        if (!currentUser?.id) {
            return;
        }

        try {
            setIsSaving(true);
            setFeedback(null);
            const payload = buildSettingsPayload(draftSettingsRef.current);
            const updated = await userProfileService.updateSettings(currentUser.id, payload);
            let normalizedUserSettings = normalizeSettings(draftSettingsRef.current);

            if (updated?.settings) {
                normalizedUserSettings = normalizeSettings(updated.settings);
                setSettings(normalizedUserSettings);
            }

            committedSettingsRef.current = normalizedUserSettings;
            draftSettingsRef.current = normalizedUserSettings;
            syncSettingsSnapshot(normalizedUserSettings);
            setFeedback({ type: "success", messageKey: "profile.feedback.saveSuccess" });
            onClose();
        } catch (err) {
            console.error("Error saving settings:", err);
            setFeedback({ type: "error", messageKey: "profile.feedback.saveError" });
        } finally {
            setIsSaving(false);
        }
    };

    const handleStartPasswordSetup = async () => {
        try {
            setIsStartingPasswordSetup(true);
            setFeedback(null);
            await accountService.startPasswordSetup();
            setFeedback({ type: "success", messageKey: "profile.feedback.passwordSetupEmailSent" });
        } catch (err) {
            console.error("Error starting password setup:", err);
            setFeedback({ type: "error", messageKey: "profile.feedback.passwordSetupEmailError" });
        } finally {
            setIsStartingPasswordSetup(false);
        }
    };

    const tabs = useMemo(() => ([
        { id: "privacy", label: t("profile.tabs.privacy") },
        { id: "security", label: t("profile.tabs.security") },
        { id: "appearance", label: t("profile.tabs.appearance") },
    ]), [t]);

    const renderTabContent = (activeTab) => {
        switch (activeTab) {
            case "privacy":
                return (
                    <PrivacySettings
                        settings={settings.privacy}
                        onChange={(newSettings) => handleSettingsChange("privacy", newSettings)}
                        onDeleteAccount={() => setShowAccountDeletion(true)}
                    />
                );
            case "security":
                return (
                    <SecuritySettings
                        passwordSet={currentUser?.passwordSet !== false}
                        passwordActionBusy={isStartingPasswordSetup}
                        onChangePassword={() => setShowChangePassword(true)}
                        onSetPassword={handleStartPasswordSetup}
                        onManageSessions={() => setShowActiveSessions(true)}
                    />
                );
            case "appearance":
                return (
                    <AppearanceSettings
                        settings={settings.appearance}
                        onChange={(newSettings) => handleSettingsChange("appearance", newSettings)}
                    />
                );
            default:
                return null;
        }
    };

    const isBusy = isSaving || isFetching || isStartingPasswordSetup;
    const portalRoot = typeof document !== "undefined" ? document.getElementById("portal") : null;

    if (!currentUser || !portalRoot) {
        return null;
    }

    return ReactDom.createPortal(
        <>
            <PopupOverlay>
                <Card
                    className="edit-profile-card"
                    title={<strong className="edit-profile-card__title">{t("profile.editTitle")}</strong>}
                    body={(
                        <div className="edit-profile-card__body">
                            {feedback && (
                                <div
                                    className={`settings-feedback settings-feedback--${feedback.type}`}
                                    role={feedback.type === "error" ? "alert" : "status"}
                                >
                                    {feedback.messageKey ? t(feedback.messageKey) : null}
                                </div>
                            )}
                            <div className="edit-profile-card__viewport">
                                {isFetching ? (
                                    <div className="settings-state" role="status" aria-live="polite">
                                        {t("profile.loading")}
                                    </div>
                                ) : (
                                    <div className="edit-profile-card__tabs" role="presentation">
                                        <TabbedLayout tabs={tabs}>
                                            {(activeTab) => renderTabContent(activeTab)}
                                        </TabbedLayout>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    footer={(
                        <div className="edit-profile-card__actions">
                            <Button
                                variant="secondary"
                                shape="pill"
                                className="edit-profile-card__action"
                                onClick={handleCancelClick}
                                disabled={isBusy}
                            >
                                {t("common.cancel")}
                            </Button>
                            <Button
                                variant="primary"
                                shape="pill"
                                className="edit-profile-card__action"
                                onClick={handleSaveClick}
                                disabled={isBusy}
                            >
                                {isSaving ? t("common.saving") : t("common.save")}
                            </Button>
                        </div>
                    )}
                />
            </PopupOverlay>
            {showChangePassword && (
                <ChangePasswordModal
                    onClose={() => setShowChangePassword(false)}
                    onSuccess={() => setFeedback({ type: "success", messageKey: "profile.feedback.passwordUpdated" })}
                />
            )}
            {showActiveSessions && (
                <ActiveSessionsModal
                    onClose={() => setShowActiveSessions(false)}
                    onCurrentSessionTerminated={() => {
                        void clearUserScopedClientState({ dispatch });
                        setShowActiveSessions(false);
                        onClose();
                    }}
                />
            )}
            {showAccountDeletion && (
                <AccountDeletionModal onClose={() => setShowAccountDeletion(false)} />
            )}
        </>,
        portalRoot
    );
};

EditProfile.propTypes = {
    onClose: PropTypes.func.isRequired,
};

export default EditProfile;
